import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../Modal';
import { RSButton } from 'reactsymbols-kit';
import Styles from '../../../styles/colors';
import ModelSettingsForm, { type ModelSettings } from '../../ModelSettingsForm';
import { useStores } from '../../../hooks/useStores';
import type { IModelInformation } from '../../../stores/projects/model_information';
import { rsValue } from '../../Inspector/rsForm';

/** Props for the inner {@link SaveAs} content. */
interface SaveAsContentProps {
  /** The model to copy into the new project. */
  model: IModelInformation;
  /** Close the modal. */
  onRequestClose: () => void;
}

/** Props for the {@link SaveAsModal} wrapper (the default export). */
interface SaveAsModalProps extends SaveAsContentProps {
  /** Whether the modal is open. */
  isOpen: boolean;
}

/**
 * Create a new project as a copy of the current one.
 *
 * `createProject` was injected via mobx-react (store.projects.createProject) and
 * the navigation used the withRouter shim's history.push; both are now consumed
 * directly through useStores() and useNavigate().
 */
function SaveAs({ model, onRequestClose }: SaveAsContentProps) {
  const store = useStores();
  const createProject = store.projects.createProject;
  const navigate = useNavigate();

  // The settings fields are edited together by ModelSettingsForm via partial
  // merge updates (onChangeProperty did this.setState(change)). Kept in a single
  // object spread-merged into, to preserve the class's setState merge semantics.
  const [values, setValues] = useState<ModelSettings>({
    name: `Copy of ${model.name}`,
    notation: model.notation,
    generateMethods: model.generateMethods,
    propagateOnDependency: model.propagateOnDependency,
    propagateOnMethod: model.propagateOnMethod,
  });
  const [saving, setSaving] = useState(false);
  // `error` was tracked in class state but never rendered; preserved as a no-op
  // setter so the catch branch keeps the same shape.
  const [, setError] = useState(false);

  // handler for the save button -> actually create the new project
  const onSave = () => {
    if (createProject) {
      setSaving(true);
      createProject(
        values.name,
        {
          notation: values.notation,
          generateMethods: values.generateMethods,
          propagateOnDependency: values.propagateOnDependency,
          propagateOnMethod: values.propagateOnMethod,
        },
        // Legacy behaviour: Save as... opens for a loaded model, so the active
        // version (and its content) is present. The store interface types
        // `activeVersion` as possibly-undefined; assert non-null to keep
        // byte-identical runtime without adding a guard.
        model.activeVersion!.content
      )
        .then((projectId: string) => {
          // Created: close the modal and go the newly created project
          onRequestClose();
          navigate(`/project/${projectId}`);
        })
        .catch(() => {
          setSaving(false);
          setError(true);
        });
    }
  };

  // onSubmit handler to allow 'enter' to work in the form
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!saving) {
      onSave();
    }
  };

  // change handler for the ModelSettingsForm (partial merge update)
  const onChangeProperty = (change: Partial<ModelSettings>) => {
    setValues((current) => ({ ...current, ...change }));
  };

  return (
    <div className="merlin-modal change-settings-modal">
      <div className="title">Save as...</div>
      <form className="form" onSubmit={onSubmit}>
        <div className="modal-body">
          <div className="container">
            <ModelSettingsForm
              onChange={onChangeProperty}
              values={{
                name: values.name,
                notation: values.notation,
                generateMethods: values.generateMethods,
                propagateOnMethod: values.propagateOnMethod,
                propagateOnDependency: values.propagateOnDependency,
              }}
              disabled={saving}
            />
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            type="button"
            value="Cancel"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onRequestClose}
          />
          <RSButton
            type="submit"
            value={rsValue(saving ? null : 'Save')}
            iconName={rsValue(saving ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(saving ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
}

// Wrap in a Modal
const SaveAsModal = ({ isOpen, onRequestClose, ...rest }: SaveAsModalProps) => (
  <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
    <SaveAs onRequestClose={onRequestClose} {...rest} />
  </Modal>
);

export default SaveAsModal;
